import { useEffect, useState } from "react";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sparkles, BookOpen, Clock, Compass, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { Seo } from "@/components/seo/Seo";
import { useLocalizedField } from "@/hooks/useLocalizedField";

interface BootcampRow {
  id: string;
  slug: string;
  duration_weeks: number | null;
  level: string | null;
  title_en: string | null;
  title_es: string | null;
  title_fr: string | null;
  title_pt: string | null;
  subtitle_en: string | null;
  subtitle_es: string | null;
  subtitle_fr: string | null;
  subtitle_pt: string | null;
  track_id: string | null;
}

const NEXT_STEPS = [
  { id: "browse", icon: Compass },
  { id: "ai", icon: Sparkles },
  { id: "community", icon: MessageSquare },
] as const;

const OnboardingComplete = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const localized = useLocalizedField();
  const [done, setDone] = useState(false);
  const [profile, setProfile] = useState<{
    persona: string | null;
    stage: string | null;
    preferred_track_ids: string[] | null;
    first_name: string | null;
  } | null>(null);

  // Mark onboarding complete + load profile context
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: existing } = await supabase
        .from("profiles")
        .select("persona, stage, preferred_track_ids, first_name, onboarding_completed_at")
        .eq("id", u.user.id)
        .maybeSingle();
      setProfile(existing ?? null);
      if (!existing?.onboarding_completed_at) {
        const { error } = await supabase
          .from("profiles")
          .update({ onboarding_completed_at: new Date().toISOString() })
          .eq("id", u.user.id);
        if (error) {
          toast.error(t("onboarding.errors.saveFailed"));
        }
      }
      setDone(true);
    })();
  }, [navigate, t]);

  const { data: recs, isLoading } = useQuery<BootcampRow[]>({
    enabled: done,
    queryKey: ["onboarding-recs", profile?.preferred_track_ids?.join(",") ?? "", profile?.stage ?? ""],
    queryFn: async () => {
      const trackIds = profile?.preferred_track_ids ?? [];
      let query = supabase
        .from("bootcamps")
        .select(
          "id,slug,duration_weeks,level,track_id,title_en,title_es,title_fr,title_pt,subtitle_en,subtitle_es,subtitle_fr,subtitle_pt",
        )
        .eq("published", true)
        .limit(3);
      if (trackIds.length > 0) {
        query = query.in("track_id", trackIds);
      }
      const { data, error } = await query;
      if (error) throw error;
      if (data && data.length > 0) return data as BootcampRow[];
      // fallback: top 3 published
      const { data: fallback } = await supabase
        .from("bootcamps")
        .select(
          "id,slug,duration_weeks,level,track_id,title_en,title_es,title_fr,title_pt,subtitle_en,subtitle_es,subtitle_fr,subtitle_pt",
        )
        .eq("published", true)
        .limit(3);
      return (fallback ?? []) as BootcampRow[];
    },
  });

  const personaKey = profile?.persona && ["explorer", "series_a_plus", "seed_founder", "bootstrapper"].includes(profile.persona)
    ? profile.persona
    : "explorer";

  return (
    <>
      <Seo title={t("onboarding.complete.seoTitle")} />
      <OnboardingShell step={4}>
        <div className="text-center">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl bg-primary text-primary-foreground shadow-lg">
            <Sparkles className="h-10 w-10" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-5xl">
            {t("onboarding.complete.title", {
              name: profile?.first_name ?? t("onboarding.complete.fallbackName"),
            })}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {t(`onboarding.complete.personaSummary.${personaKey}`)}
          </p>
        </div>

        {/* Recommendations */}
        <section className="mx-auto mt-12 max-w-4xl">
          <div className="flex items-baseline justify-between">
            <div>
              <h2 className="font-display text-xl font-bold">{t("onboarding.complete.recsTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("onboarding.complete.recsSubtitle")}</p>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {isLoading
              ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)
              : recs?.length
                ? recs.map((b, i) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => navigate(`/catalog/${b.slug}`)}
                      className="group relative rounded-xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg"
                    >
                      {i === 0 && (
                        <Badge variant="secondary" className="absolute -top-2 right-3">
                          {t("onboarding.complete.priorityStart")}
                        </Badge>
                      )}
                      <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                        <BookOpen className="h-5 w-5" />
                      </div>
                      <h3 className="mt-3 line-clamp-2 font-semibold leading-snug">
                        {localized(b, "title")}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                        {localized(b, "subtitle")}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {b.duration_weeks ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {b.duration_weeks} {t("onboarding.complete.weeks")}
                          </span>
                        ) : null}
                        {b.level ? (
                          <span className="rounded bg-muted px-2 py-0.5 capitalize">{b.level}</span>
                        ) : null}
                      </div>
                    </button>
                  ))
                : (
                    <p className="col-span-full text-sm text-muted-foreground">
                      {t("onboarding.complete.noRecs")}
                    </p>
                  )}
          </div>
        </section>

        {/* Next steps */}
        <section className="mx-auto mt-12 max-w-4xl">
          <h2 className="font-display text-xl font-bold">{t("onboarding.complete.nextStepsTitle")}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {NEXT_STEPS.map(({ id, icon: Icon }) => (
              <div key={id} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t(`onboarding.complete.nextSteps.${id}.title`)}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t(`onboarding.complete.nextSteps.${id}.desc`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTAs */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" onClick={() => navigate("/dashboard")}>
            {t("onboarding.complete.primaryCta")} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => navigate("/catalog")}>
            {t("onboarding.complete.secondaryCta")}
          </Button>
        </div>
      </OnboardingShell>
    </>
  );
};

export default OnboardingComplete;
